import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, MousePointerClick, Link as LinkIcon, RefreshCw } from "lucide-react";

const RANGES = [
  { key: "24h", label: "24 hours", hours: 24 },
  { key: "7d",  label: "7 days",   hours: 24 * 7 },
  { key: "30d", label: "30 days",  hours: 24 * 30 },
  { key: "all", label: "All time", hours: null },
];

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function hostnameOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

export default function Analytics() {
  const [data, setData] = useState({ links: [], by_target: {}, by_draft: {} });
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/promo-clicks");
      setData(r.data || { links: [], by_target: {}, by_draft: {} });
    } catch (e) {
      // empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cutoffMs = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    return r?.hours ? Date.now() - r.hours * 3600 * 1000 : 0;
  }, [range]);

  const enrichedLinks = useMemo(() => {
    return (data.links || []).map((l) => {
      const clicks = (l.clicks || []).filter((c) => !cutoffMs || new Date(c.timestamp).getTime() >= cutoffMs);
      return { ...l, click_count_in_range: clicks.length, clicks };
    }).sort((a, b) => b.click_count_in_range - a.click_count_in_range);
  }, [data, cutoffMs]);

  const totalClicks = enrichedLinks.reduce((s, l) => s + l.click_count_in_range, 0);
  const totalLinks = enrichedLinks.length;
  const activeLinks = enrichedLinks.filter((l) => l.click_count_in_range > 0).length;

  const byTarget = useMemo(() => {
    const map = {};
    for (const l of enrichedLinks) {
      const k = l.target_url || "unknown";
      map[k] = (map[k] || 0) + l.click_count_in_range;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [enrichedLinks]);

  // 14-day sparkline buckets (only for "30d" or "all" + minimum 7 days range)
  const sparkline = useMemo(() => {
    const buckets = Array(14).fill(0);
    const dayMs = 86400 * 1000;
    const now = Date.now();
    for (const l of (data.links || [])) {
      for (const c of (l.clicks || [])) {
        const t = new Date(c.timestamp).getTime();
        const dayIdx = 13 - Math.floor((now - t) / dayMs);
        if (dayIdx >= 0 && dayIdx < 14) buckets[dayIdx] += 1;
      }
    }
    const max = Math.max(1, ...buckets);
    return { buckets, max };
  }, [data]);

  return (
    <div className="fade-in" data-testid="analytics-page">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-4">Click analytics</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
            Where the clicks land.
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-white/10">
            {RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`analytics-range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-2.5 sm:px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
                  range === r.key ? "bg-[#FF3B00]/15 text-[#FF3B00]" : "text-white/55 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            className="btn-ghost-line flex items-center gap-2"
            onClick={load}
            data-testid="analytics-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
        <Kpi label="Total clicks" value={totalClicks} testId="kpi-total-clicks" />
        <Kpi label="Active links" value={`${activeLinks} / ${totalLinks}`} testId="kpi-active-links" />
        <Kpi label="Avg. CTR per link" value={totalLinks ? (totalClicks / totalLinks).toFixed(1) : "0"} testId="kpi-avg-ctr" />
      </div>

      {/* Sparkline */}
      <div className="panel p-5 sm:p-6 mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="label-mono">Last 14 days</div>
          <div className="label-mono text-white/40">{sparkline.buckets.reduce((s, x) => s + x, 0)} total clicks</div>
        </div>
        <div className="flex items-end gap-1 h-24" data-testid="analytics-sparkline">
          {sparkline.buckets.map((v, i) => (
            <div
              key={i}
              className="flex-1 bg-[#FF3B00]/20 hover:bg-[#FF3B00]/40 transition-colors relative group"
              style={{ height: `${(v / sparkline.max) * 100}%`, minHeight: v > 0 ? "4px" : "1px" }}
            >
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 text-[10px] font-mono text-white/0 group-hover:text-white whitespace-nowrap pointer-events-none">
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Links table */}
      <div className="panel p-5 sm:p-6 mb-10">
        <div className="label-mono mb-5 flex items-center gap-2">
          <LinkIcon size={12} /> Top short links
        </div>
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : enrichedLinks.length === 0 ? (
          <div className="text-white/55 text-sm">
            No CTAs tracked yet. Add <span className="text-white">Hypeddit / Spotify pre-save / smart-link</span> URLs to a release in <span className="text-white">Releases</span> and Alchemist Lab will inject trackable short links into every promo draft.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1 sm:mx-0">
          <table className="w-full text-sm" data-testid="analytics-links-table">
            <thead>
              <tr className="text-left label-mono text-white/45 border-b border-white/10">
                <th className="pb-3 font-normal">Code</th>
                <th className="pb-3 font-normal">Target</th>
                <th className="pb-3 font-normal">Label</th>
                <th className="pb-3 font-normal text-right">Clicks</th>
                <th className="pb-3 font-normal text-right">Last click</th>
                <th className="pb-3 font-normal text-right" />
              </tr>
            </thead>
            <tbody>
              {enrichedLinks.slice(0, 30).map((l) => (
                <tr key={l.code} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3 font-mono text-xs text-[#FF3B00]">/r/{l.code}</td>
                  <td className="py-3 truncate max-w-[280px]" title={l.target_url}>
                    <span className="text-white/80">{hostnameOf(l.target_url)}</span>
                    <span className="text-white/35 ml-1">{(l.target_url || "").replace(/^https?:\/\/(www\.)?/, "").slice(hostnameOf(l.target_url).length, 60)}</span>
                  </td>
                  <td className="py-3 text-white/55">{l.label || "—"}</td>
                  <td className="py-3 text-right font-mono text-white">{l.click_count_in_range}</td>
                  <td className="py-3 text-right label-mono">
                    {l.clicks?.[0]?.timestamp ? relTime(l.clicks[0].timestamp) : "—"}
                  </td>
                  <td className="py-3 text-right">
                    <a
                      href={l.target_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/35 hover:text-white inline-flex items-center"
                      data-testid={`analytics-open-${l.code}`}
                    >
                      <ExternalLink size={13} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Top destinations */}
      <div className="panel p-5 sm:p-6">
        <div className="label-mono mb-5 flex items-center gap-2">
          <MousePointerClick size={12} /> Top destinations
        </div>
        {byTarget.length === 0 ? (
          <div className="text-white/40 text-sm">Nothing yet — clicks will populate this view.</div>
        ) : (
          <ul className="space-y-3">
            {byTarget.map(([url, n]) => {
              const pct = totalClicks ? (n / totalClicks) * 100 : 0;
              return (
                <li key={url} data-testid={`analytics-target-row`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-sm truncate max-w-[80%]" title={url}>
                      <span className="text-white/85">{hostnameOf(url)}</span>
                      <span className="text-white/35 ml-2">{url.replace(/^https?:\/\/(www\.)?/, "").slice(hostnameOf(url).length, 80)}</span>
                    </div>
                    <div className="font-mono text-xs text-white">{n} · {pct.toFixed(0)}%</div>
                  </div>
                  <div className="h-1 bg-white/5">
                    <div className="h-full bg-[#FF3B00]" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="panel p-5 sm:p-6" data-testid={testId}>
      <div className="label-mono mb-3">{label}</div>
      <div className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight">{value}</div>
    </div>
  );
}
